import { GetIssueRelationshipsResponseDto } from '@issuemanager/api-client';
import {
  IssueStatusChip,
  LoadingProgress,
} from '@issuemanager/apps/client/components';
import { useSetHeaderProps } from '@issuemanager/apps/client/contexts';
import { getIssueHeaderTitle } from '@issuemanager/apps/client/utils';
import { IssueFieldValue } from '@issuemanager/constants';
import { Box, Button, Stack, Typography, styled } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useIssueAuthorization from '../hooks/useIssueAuthorization';
import { useIssueOutletContext } from '../hooks/useIssueOutletContext';
import { RelationshipsEmptyState, RelationshipsTable } from './components';
import AddRelationshipModal from './components/add-relationship-modal';
import RemoveRelationshipModal from './components/remove-relationship-modal';

const StyledContainer = styled(Stack)`
  margin: ${({ theme }) => theme.spacing(3)};
  gap: ${({ theme }) => theme.spacing(3)};
`;

export default function Relationships(): React.JSX.Element {
  const { t } = useTranslation(['issue', 'common']);
  const { code, issue, relationships } = useIssueOutletContext();
  const setHeaderProps = useSetHeaderProps();

  const readableFields = useMemo(
    () => (issue.data?.readableFields ?? []) as readonly IssueFieldValue[],
    [issue.data?.readableFields],
  );

  const { canManageRelationships } = useIssueAuthorization(
    code,
    issue.data?.status,
    readableFields,
  );

  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [relationshipToRemove, setRelationshipToRemove] =
    useState<GetIssueRelationshipsResponseDto | null>(null);

  useEffect(() => {
    if (!issue.data) return;

    setHeaderProps({
      viewTitle: {
        breadcrumbSections: [{ url: '/', title: t('common:appName') }],
        title: getIssueHeaderTitle({
          code,
          title: issue.data.title,
        }),
      },
      chip: issue.data?.status ? (
        <IssueStatusChip
          status={issue.data.status}
          isInitiated={issue.data.isInitiated}
        />
      ) : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.dataUpdatedAt, code]);

  const handleAddRelationship = (): void => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = (): void => {
    setIsAddModalOpen(false);
  };

  const handleOpenRemoveModal = (
    relationship: GetIssueRelationshipsResponseDto,
  ): void => {
    setRelationshipToRemove(relationship);
  };

  const handleCloseRemoveModal = (): void => {
    setRelationshipToRemove(null);
  };

  if (issue.isFetching) {
    return <LoadingProgress height={500} />;
  }

  return (
    <>
      <StyledContainer data-testid='relationships-tab'>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h4' fontWeight={600}>
            {t('issue:relationships.objectsLinked')}
          </Typography>
          {canManageRelationships && (
            <Button
              variant='contained'
              color='primary'
              onClick={handleAddRelationship}
              data-testid='add-relationship-button'
            >
              {t('issue:relationships.addRelationship')}
            </Button>
          )}
        </Box>

        <Stack spacing={2}>
          {relationships.length === 0 ? (
            <RelationshipsEmptyState
              onAddRelationship={handleAddRelationship}
              readOnly={!canManageRelationships}
            />
          ) : (
            <RelationshipsTable
              relationships={relationships}
              onRemoveClick={handleOpenRemoveModal}
              readOnly={!canManageRelationships}
            />
          )}
        </Stack>
      </StyledContainer>
      <AddRelationshipModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
      />
      <RemoveRelationshipModal
        isOpen={!!relationshipToRemove}
        relationship={relationshipToRemove}
        code={code}
        onClose={handleCloseRemoveModal}
      />
    </>
  );
}
